import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { blogApi, BlogPost } from '@/services/apiService';
import { Calendar, ArrowLeft } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import CartDrawer from '@/components/CartDrawer';
import { Button } from '@/components/ui/button';

const BlogPostPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const { isCartOpen, setIsCartOpen } = useCart();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (slug) {
            blogApi.getBySlug(slug).then(data => {
                setPost(data);
                setLoading(false);
            }).catch(() => {
                setError(true);
                setLoading(false);
            });
        }
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header onCartClick={() => setIsCartOpen(true)} />
                <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-16 flex items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </main>
                <Footer />
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header onCartClick={() => setIsCartOpen(true)} />
                <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-16 text-center">
                    <h1 className="text-3xl font-bold mb-4">Nie znaleziono wpisu</h1>
                    <Button asChild>
                        <Link to="/blog">Wróć na bloga</Link>
                    </Button>
                </main>
                <Footer />
            </div>
        );
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt || '',
        "image": post.image ? [window.location.origin + post.image] : [],
        "datePublished": post.createdAt,
        "dateModified": post.updatedAt || post.createdAt,
        "author": [{
            "@type": "Organization",
            "name": "Włoska Piwnica",
            "url": window.location.origin
        }]
    };

    return (
        <>
            <SEO 
                title={`${post.title} - Włoska piwnica`}
                description={post.excerpt || post.title}
                canonicalUrl={`/blog/${post.id}`}
                jsonLd={jsonLd}
            />
            <div className="min-h-screen flex flex-col">
                <Header onCartClick={() => setIsCartOpen(true)} />

                <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-16">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
                        <Link 
                            to="/blog" 
                            className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" /> 
                            Wróć na bloga
                        </Link>

                        <article>
                            <header className="mb-10">
                                <div className="flex items-center text-muted-foreground mb-4">
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {new Date(post.createdAt.replace(' ', 'T')).toLocaleDateString('pl-PL')}
                                </div>
                                <h1 className="text-4xl font-display font-bold text-foreground sm:text-5xl mb-6 leading-tight">
                                    {post.title}
                                </h1>
                                {post.excerpt && (
                                    <p className="text-xl text-muted-foreground leading-relaxed">
                                        {post.excerpt}
                                    </p>
                                )}
                            </header>

                            {post.image && (
                                <div className="w-full aspect-video rounded-xl overflow-hidden mb-12">
                                    <img 
                                        src={post.image} 
                                        alt={post.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}

                            <div 
                                className="prose prose-lg prose-gray max-w-none dark:prose-invert prose-headings:font-display prose-a:text-primary blog-post-content"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                            />

                            <div className="mt-16 pt-8 border-t border-border flex justify-center">
                                <Link 
                                    to="/blog" 
                                    className="inline-flex items-center text-base font-semibold text-primary hover:text-primary/80 transition-colors duration-300 group"
                                >
                                    <ArrowLeft className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1" /> 
                                    Wróć na bloga
                                </Link>
                            </div>
                        </article>
                    </div>
                </main>

                <Footer />
                <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
            </div>
        </>
    );
};

export default BlogPostPage;
